'use client';

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import competitorComparisons from 'data/competitorComparisons';

const CompetitorComparison = () => {
  const { competitors, features, hero, cta } = competitorComparisons;

  const availableCompetitors = useMemo(() => competitors.filter((c) => !c.isCylerian), [competitors]);
  const [selectedCompetitorKey, setSelectedCompetitorKey] = useState(availableCompetitors[0]?.key || '');
  const selectedCompetitor = useMemo(
    () => availableCompetitors.find((c) => c.key === selectedCompetitorKey) || availableCompetitors[0],
    [availableCompetitors, selectedCompetitorKey]
  );

  if (!selectedCompetitor) {
    return null;
  }

  // Helper function to render cell value
  const renderCellValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? (
        <svg className="check-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
        </svg>
      ) : (
        <svg className="cross-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
        </svg>
      );
    }
    return <span className="cell-text">{value || 'N/A'}</span>;
  };

  return (
    <section className="competitor-comparison">
      {/* Hero Section */}
       <motion.div 
         className="comparison-hero"
         initial={{ opacity: 0, y: 20 }}
         animate={{ opacity: 1, y: 0 }}
         transition={{ duration: 0.6 }}
       >
         <div className="container">
           <h1 className="title">
             {hero.title.split(' ').map((word, idx) => 
               word === 'Cylerian' ? (
                 <span key={idx} className="highlight"> {word}</span>
               ) : (
                 <span key={idx}> {word}</span>
               )
             )}
           </h1>
           <p className="subtitle">{hero.subtitle}</p>
           <p className="description">{hero.description}</p>
         </div>
       </motion.div>

      {/* Comparison Table */}
      <div className="comparison-content">
        <div className="container">
          <div className="comparison-controls">
            <label htmlFor="competitor-select" className="select-label">Compare Cylerian with</label>
            <select
              id="competitor-select"
              value={selectedCompetitor.key}
              onChange={(e) => setSelectedCompetitorKey(e.target.value)}
              className="competitor-select"
            >
              {availableCompetitors.map((competitor) => (
                <option key={competitor.key} value={competitor.key}>
                  {competitor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="comparison-table-wrapper">
            {/* Sticky Header */}
            <div className="table-header sticky-header">
              <div className="header-cell feature-column">
                {/* <span className="header-title">Features</span> */}
              </div>
              <div className="header-cell competitor-column cylerian-column">
                <span className="competitor-name">Cylerian</span>
                <span className="badge">Our Platform</span>
              </div>
              <div className="header-cell competitor-column">
                <span className="competitor-name">{selectedCompetitor.name}</span>
              </div>
            </div>

            {/* Feature Categories */}
            {features.map((category, categoryIdx) => (
              <motion.div 
                key={categoryIdx} 
                className="category-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: categoryIdx * 0.1 }}
              >
                <div className="category-header">
                  <h3 className="category-title">{category.category}</h3>
                </div>

                {/* Render direct items if they exist */}
                {category.items && category.items.map((item, itemIdx) => (
                  <div 
                    key={itemIdx} 
                    className={`table-row ${itemIdx % 2 === 0 ? 'row-even' : 'row-odd'}`}
                  >
                    <div className="feature-cell">
                      <span className="feature-name">{item.feature}</span>
                    </div>
                    <div className="value-cell cylerian-cell">
                      {renderCellValue(item.cylerian)}
                    </div>
                    <div className="value-cell">
                      {renderCellValue(item[selectedCompetitor.key] ?? item[selectedCompetitor.fallbackKey])}
                    </div>
                  </div>
                ))}

                {/* Render subcategories if they exist */}
                {category.subcategories && category.subcategories.map((subcategory, subIdx) => (
                  <div key={subIdx} className="subcategory-wrapper">
                    <div className="subcategory-header">
                      <h4 className="subcategory-title">{subcategory.name}</h4>
                    </div>
                    {subcategory.items.map((item, itemIdx) => (
                      <div 
                        key={itemIdx} 
                        className={`table-row ${itemIdx % 2 === 0 ? 'row-even' : 'row-odd'}`}
                      >
                        <div className="feature-cell">
                          <span className="feature-name">{item.feature}</span>
                        </div>
                        <div className="value-cell cylerian-cell">
                          {renderCellValue(item.cylerian)}
                        </div>
                        <div className="value-cell">
                          {renderCellValue(item[selectedCompetitor.key] ?? item[selectedCompetitor.fallbackKey])}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
    </section>
  );
};

export default CompetitorComparison;
